import React, { useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Select, { components } from 'react-select';

import FilledButton from 'components/application/buttons/FilledButton';
import TextButton from 'components/application/buttons/TextButton';
import { DropdownIndicator } from 'components/application/CollectionSelect';
import ModalBox from 'components/application/ModalBox';
import Tooltip from 'components/application/Tooltip';

import { useBreadBoard } from 'components/contexts/Toaster';

import { formatData } from 'components/helpers/reactQuery';
import { resourceShape } from 'components/helpers/serialisableResources';

export default function EditSimproIntegrationModal({
  integratedSimproCompanyIds,
  isSaving,
  isSimproMultiCompany,
  onClose,
  onSave,
  simproCompany,
  simproCompanyIntegration,
}) {
  const [simproCompanyValue, setSimproCompanyValue] = useState(
    formatCompanyOption({ company: simproCompany }),
  );

  const { t: i18nT } = useTranslation('components', {
    keyPrefix:
      'settings.externalIntegrationConfiguration.components.EditSimproIntegrationModal',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const handleSimproCompanyChange = (option) => {
    setSimproCompanyValue(option);
  };

  return (
    <ModalBox
      customFooter={
        <div className='modalbox__footer-actions modalbox__footer-actions--right'>
          <TextButton
            className='tw-mr-3'
            color='blue'
            onClick={onClose}
            size='md'
          >
            {i18nTShared('cancel')}
          </TextButton>
          <div className='tooltip-parent'>
            <FilledButton
              color='mint'
              disabled={isSaving}
              onClick={() =>
                onSave({
                  simproCompanyId: simproCompanyValue.value,
                })
              }
            >
              {i18nTShared('saveChanges')}
            </FilledButton>
          </div>
        </div>
      }
      isOpen={true}
      mode='flexible'
      onClose={onClose}
    >
      <div className='tw-p-8'>
        <h2 className='tw-mb-4 tw-mt-0 tw-text-l tw-font-semibold tw-text-grey-900'>
          {i18nT('body.title')}
        </h2>
        <SimproCompanySelector
          integratedSimproCompanyIds={integratedSimproCompanyIds}
          isMultiValue={isSimproMultiCompany}
          onChange={handleSimproCompanyChange}
          simproCompanyIntegration={simproCompanyIntegration}
          value={simproCompanyValue}
        />
      </div>
    </ModalBox>
  );
}

EditSimproIntegrationModal.propTypes = {
  simproCompanyIntegration: resourceShape('simproCompanyIntegration')
    .isRequired,
  integratedSimproCompanyIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  isSaving: PropTypes.bool.isRequired,
  isSimproMultiCompany: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  simproCompany: resourceShape('simproCompany').isRequired,
};

function SimproCompanySelector({
  integratedSimproCompanyIds,
  isMultiValue,
  onChange,
  simproCompanyIntegration,
  value,
}) {
  const breadBoard = useBreadBoard();

  const [isDomainSimproUserQueryEnabled, setIsDomainSimproUserQueryEnabled] =
    useState(false);

  const { t: i18nT } = useTranslation('components', {
    keyPrefix:
      'settings.externalIntegrationConfiguration.components.EditSimproIntegrationModal',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const { data: domainSimproUser, isSuccess: isDomainSimproUserFetchSuccess } =
    useQuery({
      queryKey: [
        'simproCompanyIntegrations',
        simproCompanyIntegration.id,
        'simproUser',
      ],
      queryFn: async () => {
        const response = await axios.get(
          `/simpro_company_integrations/${simproCompanyIntegration.id}/simpro_user`,
        );

        if (response.data.meta.requestStatus === 'failed')
          breadBoard.addInedibleToast();

        return response.data;
      },
      enabled: isDomainSimproUserQueryEnabled,
      select: (data) =>
        formatData({
          data,
          resourceTypes: ['simproCompany'],
        }),
      refetchInterval: (data) =>
        !data || ['completed', 'failed'].includes(data.meta.requestStatus) ?
          false
        : 1000,
      refetchIntervalInBackground: true,
    });

  const simproUserAccessibleCompanies =
    isDomainSimproUserFetchSuccess ?
      domainSimproUser.data?.relationships.accessibleCompanies.data.map(
        (related) => domainSimproUser.included?.simproCompany[related.id],
      )
    : [];

  const simproCompanyOptions =
    isDomainSimproUserFetchSuccess && simproUserAccessibleCompanies ?
      simproUserAccessibleCompanies
        .map((company) => {
          const isDisabled =
            value.value !== company.id &&
            integratedSimproCompanyIds.includes(Number(company.id));

          return formatCompanyOption({ company, isDisabled });
        })
        .filter((option) => Number(option.value))
    : [];

  return (
    <div>
      <label
        className='collection-select__label tw-font-medium'
        htmlFor='simproCompany'
      >
        Simpro company
      </label>
      <div className='tooltip-parent'>
        {!isMultiValue && (
          <Tooltip
            placement='top'
            tooltip={i18nT('singleCompanySimproAccountTooltip')}
            trigger='hover'
          />
        )}
        <Select
          autoFocus={false}
          className='collection-select__select-container'
          classNamePrefix='collection-select'
          components={{ Option, DropdownIndicator, SingleValue }}
          controlShouldRenderValue={true}
          id='simproCompany'
          isDisabled={!isMultiValue}
          isSearchable={true}
          loadingMessage={() => null}
          name='simproCompany'
          noOptionsMessage={() => (
            <div className='tw-text-grey-500'>
              {(
                ['completed', 'failed'].includes(
                  domainSimproUser?.meta.requestStatus,
                )
              ) ?
                i18nTShared('noMatchingResults')
              : i18nTShared('loading...')}
            </div>
          )}
          onChange={onChange}
          onFocus={() => {
            if (isMultiValue) setIsDomainSimproUserQueryEnabled(true);
          }}
          onMenuClose={() => {
            if (isMultiValue) setIsDomainSimproUserQueryEnabled(false);
          }}
          options={simproCompanyOptions}
          styles={{
            input: (provided, _state) => ({
              ...provided,
              margin: 0,
              paddingBottom: 0,
              paddingTop: 0,
            }),
          }}
          value={value}
        />
      </div>
    </div>
  );
}

SimproCompanySelector.propTypes = {
  simproCompanyIntegration: resourceShape('simproCompanyIntegration')
    .isRequired,
  integratedSimproCompanyIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  isMultiValue: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.object,
};

export function Option(props) {
  return (
    <components.Option {...props}>
      <div
        className={classNames(
          'collection-select__option_container',
          props.isDisabled && 'tw-text-grey-300',
        )}
      >
        <span>{props.children}</span>
      </div>
    </components.Option>
  );
}

function SingleValue({ ...props }) {
  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  return (
    <components.SingleValue {...props}>
      {props.selectProps.menuIsOpen ?
        <div className='tw-text-grey-500'>{i18nTShared('search...')}</div>
      : <div>{props.children}</div>}
    </components.SingleValue>
  );
}

const formatCompanyOption = ({ company, isDisabled = false }) =>
  company ?
    { value: company.id, label: company.attributes.name, isDisabled }
  : null;
